import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MapFeatureColors } from '../../../map/core/map-object-colors';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AssignableRep, WorkItem } from './leads-board.component';
import { LeadRecordSection, NextAction, nextActionHeadline } from './lead-record';
import {
  CurrentSurfaceActionService,
  LeadEmailDraftAction,
} from '../../../services/current-surface-action.service';

export interface LeadSourceRow {
  id: number; source: string; name: string | null; address: string | null;
  matchMethod: string; matchScore: number | null;
  decision: string; isPrimarySource: boolean; createdAt: string;
}
export interface LeadContactRow {
  id: number; displayName: string | null; title: string | null;
  phone: string | null; email: string | null; source: string;
  confidence: number | null; verifiedAt: string | null;
  isPrimary: boolean; suppressed: boolean; suppressedReason: string | null;
  createdByActorType: string; createdAt: string;
}
export interface LeadActivityEntry {
  id: number; contactId: number | null; activityType: string;
  subject: string; body: string | null; occurredAt: string;
  actorType: string; actorId: number | null;
}
/** A user id this record mentions, with the name to print for it. The server
 *  sends one directory for the whole payload; see LeadActorDto. */
export interface LeadActor { userId: number; name: string; }
export interface WorkItemDetail {
  item: WorkItem;
  city: string | null; postalCode: string | null; canonicalName: string | null;
  sources: LeadSourceRow[];
  contacts: LeadContactRow[];
  activities: LeadActivityEntry[];
  actors: LeadActor[];
  convertedCustomerId: number | null; convertedAt: string | null;
}

/** Lead record tabs; the host owns the profile and edit form. */
@Component({
  selector: 'app-work-item-detail',
  templateUrl: './work-item-detail.component.html',
  styleUrls: ['./work-item-detail.component.css'],
  host: { class: 'workspace-page', '[class.workspace-page--full]': '!inline' },
})
export class WorkItemDetailComponent implements OnChanges, OnDestroy {
  /** The lead being read; null renders nothing. */
  @Input() leadId: number | null = null;
  @Input() inline = false;
  private inlineTab = 0;
  /**
   * The lead's own fields, grouped, from the page that loaded the profile.
   *
   * Handed down rather than fetched again: the page needs the profile anyway
   * for its Edit form, and two reads of one record are two chances to
   * disagree. This is also what lets `pageMode` go - the page no longer has to
   * print these itself and switch off the sections that would repeat them.
   */
  @Input() record: LeadRecordSection[] = [];
  /** What the lead is waiting for, already worked out into a state. */
  @Input() nextAction: NextAction | null = null;
  /**
   * What this account may do here. Two flags because the server enforces two
   * permissions over the one record: `LeadPipeline.Manage` for the case
   * (owner, next action, stage), `Lead.Manage` for engagement (contacts,
   * activity). One combined flag would either hide a control the server would
   * accept or offer one it would refuse with a 403.
   */
  @Input() canManagePipeline = false;
  @Input() canManageLeads = false;
  @Input() canPlanVisit = false;
  @Output() visitRequested = new EventEmitter<void>();

  get visitNavigationBlocked(): boolean {
    return this.loading || this.scheduling || this.emailing || this.showContactForm || this.showActivityForm
      || this.saving || this.emailBusy || this.engagementBusy;
  }

  requestVisit(): void {
    if (this.canPlanVisit && !this.visitNavigationBlocked) this.visitRequested.emit();
  }
  /** Who this lead can be handed to. The host already loads the roster for the
   *  board's row grouping; the drawer names owners from the same list so the two
   *  never disagree about who exists. */
  @Input() reps: AssignableRep[] = [];
  @Output() closed = new EventEmitter<void>();
  /** Emitted after a successful write so the host can refresh its list. */
  @Output() changed = new EventEmitter<void>();
  /** The page owns the lead form opened by Edit.
   *  Scheduling no longer comes through here - it has its own panel. */
  @Output() editRequested = new EventEmitter<void>();

  detail: WorkItemDetail | null = null;
  loading = false;
  loadError: string | null = null;
  saving = false;
  saveError: string | null = null;

  nextActionDraft = '';
  nextActionDueDraft = '';
  nextActionPlaceDraft = '';
  nextActionNoteDraft = '';
  /** Whether the Schedule event panel is open. */
  scheduling = false;
  /** Said after a booking lands, so success is as visible as failure. */
  scheduleSaved: string | null = null;

  /** One record section by title, so a tab shows only the fields it is about
   *  and a section nobody placed cannot vanish silently. */
  recordSection(title: string): LeadRecordSection | undefined {
    return this.record.find(section => section.title === title);
  }

  readonly locationPinColor = MapFeatureColors.leads;

  get recordLocation() {
    return this.recordSection('Address')?.rows.find(row => row.location)?.location;
  }

  /** The sentence the Deal tab leads with. Absent while the profile is still
   *  loading, which is not the same as "nothing scheduled". */
  get nextActionHeadline(): string {
    return this.nextAction ? nextActionHeadline(this.nextAction) : '';
  }

  private loadSeq = 0;
  private loadRequest?: Subscription;
  private unregisterEmailComposer?: () => void;

  /**
   * Which tab is open, kept in the URL.
   *
   * Leaving for the lead form and coming back used to land on the first tab,
   * whatever you were reading when you left. The tab is part of where you
   * were, so it lives where the rest of "where you were" lives - and a link
   * to a lead's quotes is now a link somebody can send.
   */
  get selectedTab(): number {
    if (this.inline) return this.inlineTab;
    const raw = Number(this.route.snapshot.queryParamMap.get('tab'));
    return Number.isInteger(raw) && raw >= 0 ? raw : 0;
  }

  onTabChange(index: number): void {
    if (this.inline) { this.inlineTab = index; return; }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: index || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,       // a tab is not a place to press Back to
    });
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private readonly route: ActivatedRoute,
    private readonly surfaceActions?: CurrentSurfaceActionService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // `wd-drawer-open` used to be set here so the app shell's card gave up the
    // drawer's width. There is no drawer any more - this renders inside a
    // routed page - and leaving the class on narrowed the workspace for a
    // panel that was never beside it.
    if (changes['leadId']) this.registerEmailComposer();
    if (this.leadId == null) { this.detail = null; return; }
    // Reload only for a different lead. This hook fires for EVERY input - the
    // roster, the stage list, a permission flag - and reloading on all of them
    // once put this page in a loop: a parent getter handed down a fresh array
    // each change-detection pass, each pass reloaded, each response ran
    // another pass. What the server knows about the lead changes with leadId
    // and with nothing else this component is handed.
    if (changes['leadId']) this.load();
  }

  ngOnDestroy(): void {
    this.loadRequest?.unsubscribe();
    this.unregisterEmailComposer?.();
    this.unregisterEmailComposer = undefined;
    // Leaving the page with the drawer open must not leave the card narrowed.
    document.body.classList.remove('wd-drawer-open');
  }

  private registerEmailComposer(): void {
    this.unregisterEmailComposer?.();
    this.unregisterEmailComposer = undefined;
    this.emailing = false;
    this.emailError = null;
    this.emailDraft = { to: '', subject: '', body: '' };
    if (this.leadId == null) return;

    const leadId = this.leadId;
    this.unregisterEmailComposer = this.surfaceActions?.register({
      surface: { id: 'lead-email-composer', leadId },
      applyDraft: action => this.applyAgentEmailDraft(action),
    });
  }

  load(): void {
    this.loadRequest?.unsubscribe();
    if (this.leadId == null) return;
    this.loading = true;
    this.loadError = null;
    this.saveError = null;
    const seq = ++this.loadSeq;
    this.loadRequest = this.http.get<WorkItemDetail>(`/api/lead-work-items/${this.leadId}`).subscribe({
      next: res => {
        if (seq !== this.loadSeq) return;
        this.detail = res;
        this.nextActionDraft = res.item.nextActionType ?? '';
        this.nextActionDueDraft = res.item.nextActionDue ?? '';
        this.nextActionPlaceDraft = res.item.nextActionPlace ?? '';
        this.nextActionNoteDraft = res.item.nextActionNote ?? '';
        this.loading = false;
      },
      error: err => {
        if (seq !== this.loadSeq) return;
        this.loadError = err.status === 404
          ? 'This work item is no longer available.'
          : 'Failed to load the work item.';
        this.loading = false;
      },
    });
  }

  /** Kept for the `closed` output's contract; the page's own Back button is
   *  what a person presses. */
  close(): void {
    this.closed.emit();
    this.router.navigate(['/leads']);
  }

  /** Scheduling uses the pipeline command, gated on LeadPipeline.Manage. */
  get canEditCase(): boolean {
    return this.canManagePipeline && this.detail != null;
  }

  /** Contacts and activity — `POST /api/lead-work-items/…`, gated on
   *  `Lead.Manage`. */
  get canEditEngagement(): boolean {
    return this.canManageLeads && this.detail != null;
  }


  /** The schedule panel has something worth sending. */
  get scheduleDirty(): boolean {
    if (!this.detail) return false;
    const item = this.detail.item;
    return this.nextActionDraft !== (item.nextActionType ?? '')
      || this.nextActionDueDraft !== (item.nextActionDue ?? '')
      || this.nextActionPlaceDraft !== (item.nextActionPlace ?? '')
      || this.nextActionNoteDraft !== (item.nextActionNote ?? '');
  }

  /** Opens the panel on what is already booked, so it edits rather than replaces. */
  openSchedule(): void {
    if (!this.canEditCase) return;
    this.scheduling = true;
    this.saveError = null;
    this.scheduleSaved = null;
  }

  cancelSchedule(): void {
    this.scheduling = false;
    const item = this.detail?.item;
    this.nextActionDraft = item?.nextActionType ?? '';
    this.nextActionDueDraft = item?.nextActionDue ?? '';
    this.nextActionPlaceDraft = item?.nextActionPlace ?? '';
    this.nextActionNoteDraft = item?.nextActionNote ?? '';
  }

  /**
   * Books the next action. Four fields, one command - the same
   * lead-pipeline PUT the rep goes through, so the server keeps owning
   * validation and nobody has to open the whole Edit Lead form to say
   * "site visit, Thursday 2pm".
   */
  saveSchedule(): void {
    const leadId = this.detail?.item.leadId;
    if (!this.canEditCase || leadId == null || !this.scheduleDirty) return;
    // Said before the request, because the server cannot tell an empty "what"
    // from a caller that meant to leave it alone.
    if (!this.nextActionDraft.trim()) {
      this.saveError = 'Say what the event is before scheduling it.';
      return;
    }
    if (!this.nextActionDueDraft) {
      this.saveError = 'Pick a date and time.';
      return;
    }
    this.saving = true;
    this.saveError = null;
    this.scheduleSaved = null;
    this.http.put(`/api/lead-pipeline/${leadId}`, {
      nextActionType: this.nextActionDraft.trim(),
      nextActionDue: this.nextActionDueDraft,
      nextActionPlace: this.nextActionPlaceDraft.trim() || null,
      nextActionNote: this.nextActionNoteDraft.trim() || null,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.scheduling = false;
        this.scheduleSaved = `${this.nextActionDraft.trim()} is booked.`;
        this.changed.emit();
        this.load();
      },
      error: err => {
        this.saving = false;
        this.saveError = this.reasonFor(err, 'schedule that');
      },
    });
  }

  /**
   * What actually went wrong, in the words the server used where it gave any.
   *
   * Every failure used to arrive as "could not schedule that", which tells
   * somebody nothing about what to change. The server now answers a bad
   * request with a sentence; a status this cannot interpret still says which
   * status it was, so a support conversation starts from a fact.
   */
  private reasonFor(err: { status?: number; error?: { error?: string } }, doing: string): string {
    const said = err?.error?.error;
    if (said) return said;
    switch (err?.status) {
      case 0:   return `Could not reach the server, so nothing was saved.`;
      case 403: return 'You do not have permission to change this lead.';
      case 404: return 'That lead no longer exists.';
      case 409: return 'Somebody else changed this lead; reopen it and try again.';
      default:  return `Could not ${doing} (server said ${err?.status ?? 'nothing'}).`;
    }
  }


  openCustomer(): void {
    if (this.detail?.convertedCustomerId == null) return;
    this.router.navigate(['/customers', this.detail.convertedCustomerId]);
  }

  // ── The customer account, once there is one ────────────────────────────
  //
  // The Contracts tab and its read of `/api/customers/{id}/contracts` are
  // gone. It listed the CUSTOMER's contracts, which a lead does not have: a
  // contract needs an account, and a lead has none until a quote is accepted -
  // the account is created inside that transaction. The tab is quotes now, and
  // the accepted one names the contract it produced.

  /** Null until the lead is won and converted. */
  get customerId(): number | null {
    return this.detail?.convertedCustomerId ?? null;
  }

  /**
   * The internal yes on a quote is `Automation.Approve`, which is NOT implied
   * by being allowed to work the quote. Two permissions over one screen, as
   * the server has them - one flag would either hide a decision the server
   * would accept or offer one it would refuse.
   */
  @Input() canApproveQuotes = false;

  /** Accepting a quote wins the lead and creates the account, so the record
   *  around that tab is describing something that has moved on. */
  onQuoteAccepted(): void {
    this.load();
    this.changed.emit();
  }

  // ── Contacts and activities (pre-Won engagement) ───────────────────────

  readonly activityTypes = ['Note', 'Call', 'Email', 'Visit', 'Meeting'];

  showContactForm = false;
  contactDraft = { displayName: '', title: '', phone: '', email: '', isPrimary: false };
  showActivityForm = false;
  activityDraft = { activityType: 'Note', subject: '', body: '' };
  engagementError: string | null = null;
  engagementBusy = false;

  /** A contact is only worth saving if there is something to contact. */
  get contactDraftValid(): boolean {
    const d = this.contactDraft;
    return !!(d.displayName.trim() || d.phone.trim() || d.email.trim());
  }

  get activityDraftValid(): boolean {
    return !!this.activityDraft.subject.trim();
  }

  /** The agent writes its own entries; a person's log is their own work. */
  isAgentEntry(activity: LeadActivityEntry): boolean {
    return activity.actorType === 'Agent';
  }

  contactLabel(contact: LeadContactRow): string {
    return contact.displayName || contact.email || contact.phone || `Contact #${contact.id}`;
  }

  addContact(): void {
    if (!this.canEditEngagement || this.leadId == null || !this.contactDraftValid
      || this.engagementBusy) return;
    this.engagementBusy = true;
    this.engagementError = null;
    this.http.post(`/api/lead-work-items/${this.leadId}/contacts`, {
      displayName: this.contactDraft.displayName.trim() || null,
      title: this.contactDraft.title.trim() || null,
      phone: this.contactDraft.phone.trim() || null,
      email: this.contactDraft.email.trim() || null,
      isPrimary: this.contactDraft.isPrimary,
    }).subscribe({
      next: () => {
        this.engagementBusy = false;
        this.showContactForm = false;
        this.contactDraft = { displayName: '', title: '', phone: '', email: '', isPrimary: false };
        this.load();
      },
      error: err => {
        this.engagementBusy = false;
        this.engagementError = err.error?.error
          ?? (err.status === 403 ? 'You do not have permission to add contacts.' : 'Could not save the contact.');
      },
    });
  }

  suppressContact(contact: LeadContactRow): void {
    if (!this.canEditEngagement || this.engagementBusy) return;
    this.engagementBusy = true;
    this.engagementError = null;
    this.http.post(`/api/lead-work-items/contacts/${contact.id}/suppress`,
      { reason: 'Suppressed from the work item' }).subscribe({
      next: () => { this.engagementBusy = false; this.load(); },
      error: err => {
        this.engagementBusy = false;
        this.engagementError = err.status === 403
          ? 'You do not have permission to suppress contacts.'
          : 'Could not suppress the contact.';
      },
    });
  }

  // â”€â”€ Emailing the lead â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /** Open when the composer is up. */
  emailing = false;
  emailBusy = false;
  emailError: string | null = null;
  emailDraft = { to: '', subject: '', body: '' };

  /** Addresses this lead has, so the composer offers them rather than asking. */
  get contactEmails(): string[] {
    return (this.detail?.contacts ?? [])
      .filter(contact => !contact.suppressed && !!contact.email)
      .map(contact => contact.email!);
  }

  /**
   * Opens the composer, addressed to the first contact who has an address and
   * has not been suppressed. A suppressed contact is one the product has been
   * told not to contact, so it must never be the default.
   */
  openEmail(): void {
    if (!this.canEditEngagement) return;
    this.emailing = true;
    this.emailError = null;
    this.emailDraft = {
      to: this.contactEmails[0] ?? '',
      subject: '',
      body: '',
    };
  }

  cancelEmail(): void {
    this.emailing = false;
    this.emailError = null;
  }

  /** A trusted Agent proposal still changes only local form state. It keeps
   * any recipient the rep already chose, opens the composer, and leaves the
   * existing Send command as the sole network/persistence boundary. */
  private applyAgentEmailDraft(action: LeadEmailDraftAction): boolean {
    if (!this.canEditEngagement || this.leadId !== action.leadId) return false;
    this.emailing = true;
    this.emailError = null;
    this.emailDraft = {
      ...this.emailDraft,
      subject: action.subject,
      body: action.body,
    };
    return true;
  }

  get emailValid(): boolean {
    return !!this.emailDraft.to.trim()
      && !!this.emailDraft.subject.trim()
      && !!this.emailDraft.body.trim();
  }

  /** Sends it, and lets the activity log say so. */
  sendEmail(): void {
    if (this.leadId == null || !this.emailValid || this.emailBusy) return;
    this.emailBusy = true;
    this.emailError = null;
    this.http.post(`/api/lead-work-items/${this.leadId}/emails`, {
      to: this.emailDraft.to.trim(),
      subject: this.emailDraft.subject.trim(),
      body: this.emailDraft.body,
    }).subscribe({
      next: () => {
        this.emailBusy = false;
        this.emailing = false;
        this.changed.emit();
        this.load();          // the sent mail is now an activity; show it
      },
      error: err => {
        this.emailBusy = false;
        this.emailError = err.error?.error
          ?? 'The email could not be sent, so nothing was recorded.';
      },
    });
  }
  /**
   * Open the log-activity form, and close it again.
   *
   * Two named commands rather than a toggle on the template, because the
   * button that opened it is no longer the button that closes it. It used to
   * be: the opener sat in the pane's action bar and relabelled itself Cancel,
   * so cancelling meant going back to the foot of the page to a button that
   * had changed its name under you - while the form's own submit button sat
   * where you were looking. Email and Schedule in this same pane both put
   * Cancel next to the thing it cancels; this one now does too.
   *
   * The draft survives a cancel, as the other two forms' do. Somebody who
   * closes a form by accident has not asked to lose what they typed.
   */
  openActivity(): void {
    if (!this.canEditEngagement) return;
    this.showActivityForm = true;
    this.engagementError = null;
  }

  cancelActivity(): void {
    this.showActivityForm = false;
    this.engagementError = null;
  }

  addActivity(): void {
    if (!this.canEditEngagement || this.leadId == null || !this.activityDraftValid
      || this.engagementBusy) return;
    this.engagementBusy = true;
    this.engagementError = null;
    this.http.post(`/api/lead-work-items/${this.leadId}/activities`, {
      activityType: this.activityDraft.activityType,
      subject: this.activityDraft.subject.trim(),
      body: this.activityDraft.body.trim() || null,
      contactId: null,
      occurredAt: null,
    }).subscribe({
      next: () => {
        this.engagementBusy = false;
        this.showActivityForm = false;
        this.activityDraft = { activityType: 'Note', subject: '', body: '' };
        this.load();
      },
      error: err => {
        this.engagementBusy = false;
        this.engagementError = err.error?.error
          ?? (err.status === 403 ? 'You do not have permission to log activity.' : 'Could not log the activity.');
      },
    });
  }

  // ── Who: user ids printed as people ────────────────────────────────────

  /**
   * The person behind a user id, from the directory the payload carries.
   *
   * Falls back to the id only when the directory has no row for it, which
   * means this tenant can no longer see that account — a deleted user, or one
   * moved to another company. Showing the bare id there is honest: it is
   * genuinely all that is left of them on this record.
   *
   * A scan, not an index: the directory holds the few people who touched one
   * lead, so building a map would cost more than the lookups it saves.
   */
  personName(userId: number): string {
    return this.detail?.actors?.find(actor => actor.userId === userId)?.name
      ?? `User ${userId}`;
  }





  /** Same rule for the activity log. */
  activityActor(activity: LeadActivityEntry): string {
    return activity.actorType === 'User' && activity.actorId != null
      ? this.personName(activity.actorId)
      : activity.actorType;
  }
}
